import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import { getCategoryBySlug } from '@/mocks/categories';
import { getVideosByCategory } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

function CategorySchema({ categoryName, categorySlug }: { categoryName: string; categorySlug: string }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 17c·moc起草糖心在线看`,
    description: `浏览${categoryName}分类下的所有高清视频，17c·moc起草糖心在线看免费高清视频平台。`,
    url: `https://17c-moc.com/category/${categorySlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '17c·moc起草糖心在线看',
      url: 'https://17c-moc.com',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [categoryVideos, setCategoryVideos] = useState<Video[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const videosPerPage = 12;

  const category = getCategoryBySlug(slug || '');

  useEffect(() => {
    if (slug) {
      const filtered = getVideosByCategory(slug);
      setCategoryVideos(filtered);
      setCurrentPage(1);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, currentPage]);

  const totalPages = Math.max(1, Math.ceil(categoryVideos.length / videosPerPage));
  const paginatedVideos = categoryVideos.slice(
    (currentPage - 1) * videosPerPage,
    currentPage * videosPerPage,
  );

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="container-custom text-center py-20">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的分类不存在，请返回首页浏览其他内容。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <CategorySchema categoryName={category.name} categorySlug={category.slug} />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="py-6">
          <div className="container-custom">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
                <i className={`${category.icon} text-primary-600 text-lg`} style={{ width: '22px', height: '22px' }}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-6">
          <div className="container-custom">
            <CategoryTabs />
          </div>
        </section>

        <section className="py-6 bg-background-100">
          <div className="container-custom">
            {paginatedVideos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {paginatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 rounded-lg flex items-center justify-center border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-s-line" style={{ width: '14px', height: '14px' }}></i>
                      </div>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                          currentPage === page
                            ? 'bg-primary-500 text-white'
                            : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 rounded-lg flex items-center justify-center border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line" style={{ width: '14px', height: '14px' }}></i>
                      </div>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-background-200 rounded-full flex items-center justify-center">
                  <i className="ri-inbox-line text-foreground-400 text-2xl" style={{ width: '28px', height: '28px' }}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2">暂无内容</h3>
                <p className="text-foreground-500 text-sm">该分类下暂时没有视频，请浏览其他分类</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}